import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import {
  TextField, PasswordField, OrDivider, FormError, SubmitButton,
} from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function SignupPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!accepted) {
      setErr(t('auth.error_must_accept_terms', { defaultValue: 'Please accept the terms to continue.' }) as string);
      return;
    }
    if (password.length < 8) {
      setErr(t('auth.error_password_too_short', { defaultValue: 'Password must be at least 8 characters.' }) as string);
      return;
    }
    setPending(true);
    try {
      await authApi.register({ name, email, password });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      nav('/onboarding', { replace: true });
    } catch (caught) {
      setErr(readApiError(caught, t('auth.error_signup_failed', { defaultValue: 'Sign-up failed. Please try again.' }) as string));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.signup_title', { defaultValue: 'Build your knowledge plane.' })}
      subtitle={t('auth.signup_subtitle', {
        defaultValue:
          'Free for up to 10 seats, forever. Bring your own LLM. Cancel anytime.',
      })}
      footer={
        <span>
          {t('auth.have_account', { defaultValue: 'Already have an account?' })}{' '}
          <Link to="/login" className="text-accent-700 font-semibold hover:underline">
            {t('auth.sign_in', { defaultValue: 'Sign in' })}
          </Link>
        </span>
      }
    >
      <OAuthButtons />
      <OrDivider label={t('auth.or_email', { defaultValue: 'or with email' }) as string} />

      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="name"
          label={t('auth.name_label', { defaultValue: 'Your name' }) as string}
          placeholder={t('auth.name_placeholder', { defaultValue: 'Tanaka Hiroshi / Jane Doe' }) as string}
          value={name}
          onChange={setName}
          autoComplete="name"
          autoFocus
          required
        />
        <TextField
          id="email"
          type="email"
          inputMode="email"
          label={t('auth.email_label', { defaultValue: 'Work email' }) as string}
          placeholder="you@company.com"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <PasswordField
          id="password"
          label={t('auth.password_label', { defaultValue: 'Password' }) as string}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          showStrength
          required
        />

        <label className="flex items-start gap-2.5 pt-1">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
          />
          <span className="text-[12.5px] text-ink-600 leading-relaxed">
            {t('auth.terms_pre', { defaultValue: 'I agree to the' })}{' '}
            <Link to="/legal/terms" className="text-accent-700 hover:underline">
              {t('auth.terms', { defaultValue: 'Terms of Service' })}
            </Link>{' '}
            {t('common.and', { defaultValue: 'and' })}{' '}
            <Link to="/legal/privacy" className="text-accent-700 hover:underline">
              {t('auth.privacy', { defaultValue: 'Privacy Policy' })}
            </Link>
            .
          </span>
        </label>

        <SubmitButton pending={pending}>
          {t('auth.create_workspace', { defaultValue: 'Create workspace — free' })}
        </SubmitButton>

        <p className="text-[11.5px] text-ink-500 text-center pt-1">
          {t('auth.free_note', {
            defaultValue: 'No credit card · 10 free seats forever · Cancel anytime',
          })}
        </p>
      </form>
    </AuthLayout>
  );
}
